import { useEffect, useState, type FormEvent } from "react";
import { Inbox, Lock, Mail, Save, Shield } from "lucide-react";
import {
  api,
  ApiError,
  type EmailPreferencesEnvelope,
  type Notification,
} from "../../api";
import { Button, Card, Field, PageHeader, cx } from "../../ui";
import {
  PasswordInput,
  PasswordPolicyChecklist,
  PasswordStrengthMeter,
  passwordMeetsPolicy,
} from "../../components/PasswordInput";
import { MfaCard } from "../../components/MfaCard";

export function SettingsPage() {
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSaved, setPwdSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const [history, setHistory] = useState<Notification[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    setHistoryLoading(true);
    void api
      .get<{ data: Notification[] }>("/api/notifications")
      .then((r) => setHistory(r.data))
      .finally(() => setHistoryLoading(false));
  }, []);

  const passwordsMatch =
    pwd.confirm.length > 0 && pwd.next === pwd.confirm;
  const newPasswordOk = passwordMeetsPolicy(pwd.next);
  const canSubmitPwd =
    pwd.current.length > 0 && newPasswordOk && passwordsMatch;

  const changePwd = async (e: FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    setPwdSaved(false);
    if (!newPasswordOk) {
      setPwdError("Pick a password that meets all the requirements below.");
      return;
    }
    if (!passwordsMatch) {
      setPwdError("New passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/me/password", {
        currentPassword: pwd.current,
        newPassword: pwd.next,
      });
      setPwd({ current: "", next: "", confirm: "" });
      setPwdSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string })?.error;
        setPwdError(
          code === "current_password_invalid"
            ? "Current password is incorrect."
            : code === "password_weak"
              ? "Password doesn't meet the requirements below."
              : `Change failed: ${code}`,
        );
      } else setPwdError("Change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Settings" subtitle="Security and communication preferences." />

      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
          <Lock size={18} /> Change password
        </h2>
        <p className="text-xs text-ink-500 mb-3">
          Pick a password with at least 12 characters that mixes letters,
          numbers, and a symbol. You stay signed in on this device after a
          successful change; other sessions remain active until they expire.
        </p>
        <form onSubmit={changePwd}>
          <Field label="Current password">
            <PasswordInput
              value={pwd.current}
              onChange={(v) => setPwd((p) => ({ ...p, current: v }))}
              autoComplete="current-password"
              required
            />
          </Field>
          <Field label="New password">
            <PasswordInput
              value={pwd.next}
              onChange={(v) => setPwd((p) => ({ ...p, next: v }))}
              minLength={12}
              autoComplete="new-password"
              required
            />
            <PasswordStrengthMeter value={pwd.next} />
            <PasswordPolicyChecklist value={pwd.next} />
          </Field>
          <Field
            label="Confirm new password"
            error={
              pwd.confirm.length > 0 && !passwordsMatch
                ? "Passwords don't match."
                : undefined
            }
          >
            <PasswordInput
              value={pwd.confirm}
              onChange={(v) => setPwd((p) => ({ ...p, confirm: v }))}
              minLength={12}
              autoComplete="new-password"
              required
            />
          </Field>
          {pwdSaved && <div className="alert-success">Password updated.</div>}
          {pwdError && <div className="alert-error">{pwdError}</div>}
          <Button
            variant="primary"
            type="submit"
            loading={busy}
            disabled={busy || !canSubmitPwd}
          >
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      </Card>

      <MfaCard />

      <EmailPreferencesCard />

      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
          <Shield size={18} /> In-app notifications
        </h2>
        <p className="text-ink-700 text-sm">
          You'll always see in-app notifications for project status changes,
          comments on your projects, Board votes, insurance expirations, and
          final decisions. Email opt-outs above don't affect in-app delivery.
        </p>
      </Card>

      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
          <Inbox size={18} /> Communication history
        </h2>
        {historyLoading ? (
          <p className="text-ink-500">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-ink-500 text-sm">No messages yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Template</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 50).map((n) => (
                  <tr key={n.id}>
                    <td className="text-xs text-ink-500">
                      {new Date(n.createdAt).toLocaleString()}
                    </td>
                    <td>
                      <code>{n.templateCode}</code>
                    </td>
                    <td>
                      <span className="badge bg-ink-100 text-ink-700">
                        {n.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email preferences card.
//
// Lists every preference-gated email category from the server and
// renders one toggle per row, plus a master "stop sending me any
// preference-gated email" switch up top. Security-critical mail
// (temp passwords, magic-link invites, password resets, unit-
// claim approvals) is intentionally NOT listed here — those send
// unconditionally so an opt-out can't lock anyone out of their
// account or block a contractor from receiving an upload link.
// ---------------------------------------------------------------------------
function EmailPreferencesCard() {
  const [data, setData] = useState<EmailPreferencesEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local working copy so the user can flip several toggles before
  // saving; we only POST the merged state on Save.
  const [unsubAll, setUnsubAll] = useState(false);
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get<EmailPreferencesEnvelope>(
        "/api/me/email-preferences",
      );
      setData(r);
      setUnsubAll(r.data.unsubscribedAll);
      setDraft(
        Object.fromEntries(r.data.items.map((i) => [i.key, i.enabled])),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const dirty =
    !!data &&
    (data.data.unsubscribedAll !== unsubAll ||
      data.data.items.some((i) => i.enabled !== draft[i.key]));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const next = await api.patch<EmailPreferencesEnvelope>(
        "/api/me/email-preferences",
        {
          unsubscribedAll: unsubAll,
          preferences: draft,
        },
      );
      setData(next);
      setUnsubAll(next.data.unsubscribedAll);
      setDraft(
        Object.fromEntries(next.data.items.map((i) => [i.key, i.enabled])),
      );
      setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
        <Mail size={18} /> Email preferences
      </h2>
      <p className="text-[12.5px] text-ink-500 mb-3">
        Choose which email notifications you want to receive. Security mail
        (password resets, magic-link invites, account confirmations) is sent
        unconditionally and isn't listed here.
      </p>

      {loading && !data ? (
        <p className="text-ink-500 text-sm">Loading…</p>
      ) : data ? (
        <form onSubmit={save}>
          <Field hint="Stops every preference-gated email below in one click. Security mail is unaffected.">
            <label className="inline-flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input
                type="checkbox"
                checked={unsubAll}
                onChange={(e) => setUnsubAll(e.target.checked)}
                className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
              />
              Stop sending me any of these emails
            </label>
          </Field>

          <ul
            className={cx(
              "flex flex-col gap-2 m-0 p-0 list-none mt-3 mb-4",
              unsubAll && "opacity-50",
            )}
          >
            {data.data.items.map((item) => (
              <li
                key={item.key}
                className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-sand-200 bg-white"
              >
                <input
                  type="checkbox"
                  id={`pref-${item.key}`}
                  checked={!!draft[item.key]}
                  disabled={unsubAll}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, [item.key]: e.target.checked }))
                  }
                  className="rounded border-ink-300 text-brand-700 focus:ring-brand-500 mt-0.5"
                />
                <label
                  htmlFor={`pref-${item.key}`}
                  className="flex-1 min-w-0 cursor-pointer"
                >
                  <div className="text-[13px] font-semibold text-ink-800">
                    {item.label}
                  </div>
                  <div className="text-[11.5px] text-ink-500 leading-snug">
                    {item.hint}
                  </div>
                </label>
              </li>
            ))}
          </ul>

          {saved && !dirty && (
            <div className="alert-success">Email preferences saved.</div>
          )}
          {error && <div className="alert-error">{error}</div>}

          <Button
            variant="primary"
            type="submit"
            loading={busy}
            disabled={!dirty}
          >
            <Save size={14} /> Save preferences
          </Button>
        </form>
      ) : null}
    </Card>
  );
}
